import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   TextField,
   Box,
   Typography,
   Avatar,
   Chip,
   Alert,
   CircularProgress,
   FormControlLabel,
   Switch,
   Grid,
   Divider,
} from '@mui/material';
import {
   VideoCall as VideoCallIcon,
   Schedule as ScheduleIcon,
   Person as PersonIcon,
   Close as CloseIcon,
   Send as SendIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addMinutes, format } from 'date-fns';
import { useUser } from '../../contexts/UserContext';
import { createVirtualEventRoom } from '../../services/virtualEventService';
import { collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import axios from 'axios';

interface Contact {
   id: string;
   userId: string;
   displayName: string;
   email: string;
   photoURL?: string;
   [key: string]: any;
}

interface VirtualCallSchedulerModalProps {
   open: boolean;
   onClose: () => void;
   contact: Contact | null;
}

const VirtualCallSchedulerModal: React.FC<VirtualCallSchedulerModalProps> = ({
   open,
   onClose,
   contact,
}) => {
   const { currentUser } = useUser();
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   // Form state
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [startDate, setStartDate] = useState<Date | null>(addMinutes(new Date(), 30));
   const [duration, setDuration] = useState(30); // in minutes
   const [isRecordingEnabled, setIsRecordingEnabled] = useState(false);

   // Reset form when modal opens/closes
   useEffect(() => {
      if (open && contact) {
         setTitle(`Virtual Call with ${contact.displayName}`);
         setDescription('');
         setStartDate(addMinutes(new Date(), 30));
         setDuration(30);
         setIsRecordingEnabled(false);
         setError(null);
         setSuccess(null);
      }
   }, [open, contact]);

   const handleClose = () => {
      if (!loading) {
         onClose();
      }
   };

   const validateForm = () => {
      if (!title.trim()) {
         setError('Please enter a title for the call');
         return false;
      }
      if (!startDate) {
         setError('Please select a start date and time');
         return false;
      }
      if (startDate < new Date()) {
         setError('Start date must be in the future');
         return false;
      }
      if (duration < 15 || duration > 240) {
         setError('Duration must be between 15 minutes and 4 hours');
         return false;
      }
      return true;
   };

   const handleScheduleCall = async () => {
      if (!validateForm() || !contact || !currentUser) return;

      try {
         setLoading(true);
         setError(null);

         // Calculate end date
         const endDate = addMinutes(startDate!, duration);

         // Create event data
         const eventData = {
            title: title.trim(),
            description: description.trim(),
            startDate: startDate!,
            endDate: endDate,
            isVirtual: true,
            eventType: 'virtual_call',
            organizerId: currentUser.uid,
            organizerName: currentUser.displayName || 'Unknown',
            organizerEmail: currentUser.email || '',
            participants: [
               {
                  userId: currentUser.uid,
                  email: currentUser.email,
                  displayName: currentUser.displayName || 'Unknown',
                  photoURL: currentUser.photoURL || '',
                  status: 'accepted', // Organizer is automatically accepted
                  role: 'organizer',
               },
               {
                  userId: contact.userId,
                  email: contact.email,
                  displayName: contact.displayName,
                  photoURL: contact.photoURL || '',
                  status: 'pending', // Invitee needs to accept
                  role: 'participant',
               },
            ],
            maxParticipants: 2,
            recordingEnabled: isRecordingEnabled,
            status: 'scheduled',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // Add metadata for virtual call scheduling
            isScheduledCall: true,
            invitedBy: currentUser.uid,
            invitedUser: contact.userId,
         };

         // Create the scheduled call in Firestore
         const eventRef = await addDoc(collection(db, 'scheduledCalls'), eventData);
         const eventId = eventRef.id;

         // Create Twilio room for the virtual call
         try {
            const roomResult = await createVirtualEventRoom(
               eventId,
               `${title.trim()}-${eventId}`,
               isRecordingEnabled
            );

            // Update the event with Twilio room details
            await updateDoc(eventRef, {
               virtualRoomSid: roomResult.roomSid,
               virtualRoomName: roomResult.roomName,
               virtualRoomUrl: roomResult.roomUrl,
               joinUrl: roomResult.joinUrl,
               virtualEventProvider: 'twilio',
            });
         } catch (twilioError) {
            console.error('Error creating Twilio room:', twilioError);
            // Continue without Twilio room - can be created later
         }

         // Send invitation notification
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            await axios.post(
               `${process.env.REACT_APP_API_URL}/api/notifications/send-event-invitation`,
               {
                  eventId: eventId,
                  inviteeUserId: contact.userId,
                  inviteeEmail: contact.email,
                  organizerName: currentUser.displayName || 'Someone',
                  eventTitle: title.trim(),
                  eventDate: startDate!.toISOString(),
                  eventType: 'virtual_call',
               },
               { headers }
            );
         } catch (notificationError) {
            console.error('Error sending invitation notification:', notificationError);
            // Continue without notification - the event is still created
         }

         // Send scheduled call email
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            // Format date and time for email
            const formattedDate = startDate!.toLocaleDateString('en-US', {
               weekday: 'long',
               year: 'numeric',
               month: 'long',
               day: 'numeric',
            });
            const formattedTime = startDate!.toLocaleTimeString('en-US', {
               hour: 'numeric',
               minute: '2-digit',
               hour12: true,
            });
            const formattedDuration = `${duration} minutes`;

            await axios.post(
               `${process.env.REACT_APP_API_URL}/api/notifications/send-scheduled-call-email`,
               {
                  eventId: eventId,
                  inviteeEmail: contact.email,
                  organizerName: currentUser.displayName || 'Someone',
                  eventTitle: title.trim(),
                  eventDate: formattedDate,
                  eventTime: formattedTime,
                  eventDuration: formattedDuration,
                  eventDescription: description.trim(),
                  callLink: `${process.env.REACT_APP_BASE_URL}/scheduled-call/${eventId}`,
               },
               { headers }
            );
         } catch (emailError) {
            console.error('Error sending scheduled call email:', emailError);
            // Continue without email - the event is still created
         }

         setSuccess('Virtual call scheduled successfully! An invitation has been sent.');

         // Close modal after a short delay
         setTimeout(() => {
            handleClose();
         }, 2000);
      } catch (error) {
         console.error('Error scheduling virtual call:', error);
         setError('Failed to schedule virtual call. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   if (!contact) return null;

   return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
         <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{
               sx: {
                  borderRadius: 2,
                  maxHeight: '90vh',
               },
            }}
         >
            <DialogTitle
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  pb: 1,
               }}
            >
               <VideoCallIcon color='primary' />
               <Box sx={{ flex: 1 }}>
                  <Typography variant='h6' component='div'>
                     Schedule Virtual Call
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Create a video call invitation
                  </Typography>
               </Box>
               <Button onClick={handleClose} disabled={loading} sx={{ minWidth: 'auto', p: 1 }}>
                  <CloseIcon />
               </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
               {/* Contact Info */}
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 2,
                     p: 2,
                     backgroundColor: 'grey.50',
                     borderRadius: 1,
                     mb: 3,
                  }}
               >
                  <Avatar
                     src={contact.photoURL}
                     alt={contact.displayName}
                     sx={{ width: 48, height: 48 }}
                  >
                     {contact.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                     <Typography variant='subtitle1' fontWeight='medium'>
                        {contact.displayName}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {contact.email}
                     </Typography>
                  </Box>
                  <Chip
                     icon={<PersonIcon />}
                     label='Invitee'
                     size='small'
                     color='primary'
                     variant='outlined'
                  />
               </Box>

               {/* Form Fields */}
               <Grid container spacing={3}>
                  <Grid item xs={12}>
                     <TextField
                        fullWidth
                        label='Call Title'
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder='Enter a title for your call'
                        disabled={loading}
                        required
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <TextField
                        fullWidth
                        label='Description (Optional)'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Add any notes or agenda items'
                        multiline
                        rows={3}
                        disabled={loading}
                     />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                     <DateTimePicker
                        label='Start Date & Time'
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        disabled={loading}
                        minDateTime={new Date()}
                        renderInput={(params) => <TextField {...params} fullWidth required />}
                     />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                     <TextField
                        fullWidth
                        label='Duration (minutes)'
                        type='number'
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                        disabled={loading}
                        inputProps={{
                           min: 15,
                           max: 240,
                           step: 15,
                        }}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <FormControlLabel
                        control={
                           <Switch
                              checked={isRecordingEnabled}
                              onChange={(e) => setIsRecordingEnabled(e.target.checked)}
                              disabled={loading}
                           />
                        }
                        label='Enable call recording'
                     />
                     <Typography variant='caption' display='block' color='text.secondary'>
                        Recording will be available to all participants after the call
                     </Typography>
                  </Grid>
               </Grid>

               {/* Call Summary */}
               {startDate && (
                  <>
                     <Divider sx={{ my: 3 }} />
                     <Box sx={{ p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                        <Typography variant='subtitle2' gutterBottom>
                           Call Summary
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           <strong>When:</strong> {format(startDate, 'PPP p')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           <strong>Duration:</strong> {duration} minutes
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           <strong>Participants:</strong> You and {contact.displayName}
                        </Typography>
                     </Box>
                  </>
               )}

               {/* Error/Success Messages */}
               {error && (
                  <Alert severity='error' sx={{ mt: 2 }}>
                     {error}
                  </Alert>
               )}
               {success && (
                  <Alert severity='success' sx={{ mt: 2 }}>
                     {success}
                  </Alert>
               )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
               <Button onClick={handleClose} disabled={loading} color='inherit'>
                  Cancel
               </Button>
               <Button
                  onClick={handleScheduleCall}
                  disabled={loading || !title.trim() || !startDate}
                  variant='contained'
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
               >
                  {loading ? 'Scheduling...' : 'Send Invitation'}
               </Button>
            </DialogActions>
         </Dialog>
      </LocalizationProvider>
   );
};

export default VirtualCallSchedulerModal;
